import React, { useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Search, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

/**
 * Admin: find any article by URL and hard-delete it from the network.
 *
 * Accepts:
 *   - Public URL: `https://<domain>/article/<slug>`  (also tolerates missing scheme)
 *   - Original source URL from the RSS/API feed
 *
 * Hard-delete purges: R2 media variants, R2 HTML snapshot, Cloudflare edge
 * cache for article + home + category, MongoDB row. Site's articles_count
 * decrements if the article was published. Irreversible — requires typing the
 * article's title to confirm.
 */
export default function DeleteArticle() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);         // { article, site }
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(null);       // response after delete

  async function handleLookup(e) {
    e?.preventDefault();
    setError(""); setResult(null); setDeleted(null); setConfirmText("");
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await api.get(`/articles/lookup`, { params: { url: url.trim() } });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!result?.article?.id) return;
    setDeleting(true); setError("");
    try {
      const r = await api.delete(`/articles/${result.article.id}`);
      setDeleted(r.data);
      setResult(null);
      setConfirmText("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  const confirmTarget = (result?.article?.title || result?.article?.original_title || "").trim();
  const canDelete = result && confirmTarget && confirmText.trim() === confirmTarget && !deleting;

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6">
      <header>
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">
          Delete Article
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Paste the article&apos;s public URL (or its original source URL) to find and permanently delete it.
        </p>
      </header>

      {/* URL input */}
      <form onSubmit={handleLookup} className="flex gap-2">
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://smartbudgethelp.com/article/some-slug"
          data-testid="delete-article-url-input"
          disabled={busy}
          className="flex-1 px-4 py-2.5 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-zinc-100 font-mono placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none transition-colors disabled:opacity-50"
        />
        <button
          type="submit"
          data-testid="delete-article-find-btn"
          disabled={busy || !url.trim()}
          className="px-4 py-2.5 border border-zinc-700 rounded-sm text-sm text-zinc-100 hover:border-amber-500 hover:text-amber-400 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {busy ? "Searching…" : "Find"}
        </button>
      </form>

      {error && (
        <div data-testid="delete-article-error" className="border border-red-900 bg-red-950/30 text-red-300 text-sm px-4 py-3 rounded-sm">
          {error}
        </div>
      )}

      {/* Preview + confirm */}
      {result && (
        <ArticlePreview
          result={result}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          canDelete={canDelete}
          onDelete={handleDelete}
          deleting={deleting}
          target={confirmTarget}
        />
      )}

      {/* Success */}
      {deleted && (
        <div data-testid="delete-article-success" className="border border-emerald-900 bg-emerald-950/20 text-emerald-300 text-sm px-4 py-3 rounded-sm">
          <div className="font-semibold">Article deleted.</div>
          <div className="text-xs mt-1 font-mono text-emerald-400/80">
            id: {deleted.article_id} · site: {deleted.site_id} · slug: {deleted.slug} · r2 media purged: {deleted.r2_media_deleted}
          </div>
          <div className="text-xs mt-2 text-emerald-400/70">
            Edge cache purged. Search engines will drop this URL on next crawl.
          </div>
        </div>
      )}
    </div>
  );
}

function ArticlePreview({ result, confirmText, setConfirmText, canDelete, onDelete, deleting, target }) {
  const a = result.article;
  const s = result.site;
  const thumb = a?.media?.thumb || a?.image_url;
  return (
    <div data-testid="delete-article-preview" className="border border-zinc-800 rounded-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/40 flex items-center gap-2">
        <AlertTriangle size={14} className="text-amber-500" />
        <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">
          Confirm permanent deletion
        </span>
      </div>

      <div className="p-4 flex gap-4">
        {thumb && (
          <img
            src={thumb}
            alt=""
            className="w-24 h-24 object-cover rounded-sm border border-zinc-800 shrink-0"
          />
        )}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-base font-semibold text-zinc-100 leading-snug">
            {a.title || a.original_title}
          </div>
          <div className="text-xs text-zinc-500 font-mono flex flex-wrap gap-x-3 gap-y-1">
            <span>site: <span className="text-zinc-300">{s?.name || s?.id}</span></span>
            <span>domain: <span className="text-zinc-300">{s?.domain || "—"}</span></span>
            <span>status: <span className={a.status === "published" ? "text-emerald-400" : "text-zinc-400"}>{a.status}</span></span>
            <span>category: <span className="text-zinc-300">{a.category_slug || "—"}</span></span>
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            published: {a.published_at ? new Date(a.published_at).toISOString().slice(0, 19).replace("T", " ") : "—"}
          </div>
          {a.status === "published" && s?.domain && (
            <a
              href={`https://${s.domain}/article/${a.slug}`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 mt-1"
            >
              Open on live site <ExternalLink size={11} />
            </a>
          )}
        </div>
      </div>

      <div className="px-4 py-4 border-t border-zinc-800 bg-red-950/10 space-y-3">
        <div className="text-xs text-zinc-400">
          This will permanently delete the article, purge its R2 media + HTML snapshot,
          and flush the Cloudflare edge cache. <span className="text-red-400 font-semibold">This cannot be undone.</span>
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">
            Type the article title exactly to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={target}
            data-testid="delete-article-confirm-input"
            className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-red-600 focus:outline-none transition-colors"
          />
        </div>
        <button
          onClick={onDelete}
          disabled={!canDelete}
          data-testid="delete-article-confirm-btn"
          className="w-full px-4 py-2.5 border border-red-800 bg-red-950/40 text-red-300 rounded-sm text-sm font-semibold hover:bg-red-900/50 hover:border-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          {deleting ? "Deleting…" : "Delete permanently"}
        </button>
      </div>
    </div>
  );
}
